import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Calculator, X, Loader2, ShieldCheck, AlertTriangle, TrendingUp, Search, Save, FolderOpen, Trash2, Copy, Share2, Users, Lock, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (v) => {
  try { return `${Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`; }
  catch { return "—"; }
};

/**
 * Standalone pricing/margin simulator drawer. Available in the sidebar for
 * gestor/admin only. Doesn't require a lead — the operator picks a catalog
 * row, sets the monthly cost, ticks any relevant multipliers and gets an
 * instant preview of the monthly revenue + margin classification (36/38/40%
 * thresholds from ``margin_service``).
 *
 * ``pontualContext`` (opcional): quando um Consultor abre o simulador a
 * partir de um pontual já valorizado, passamos os totais para mostrar uma
 * faixa de referência no topo — permite comparar hourly alternativos
 * contra o preço fixado, sem sair da página do lead.
 */
export default function PricingSimulatorDrawer({ open, onClose, pontualContext = null }) {
  const [catalog, setCatalog] = useState([]);
  const [multipliers, setMultipliers] = useState([]);
  const [rowId, setRowId] = useState("");
  const [monthlyCost, setMonthlyCost] = useState(0);
  const [selectedMultIds, setSelectedMultIds] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // ---- Scenarios (save/load) ----
  const [scenarios, setScenarios] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [scenarioName, setScenarioName] = useState("");
  const [scenarioDesc, setScenarioDesc] = useState("");
  const [scenarioVisibility, setScenarioVisibility] = useState("team"); // "personal" | "team"
  const [savingScenario, setSavingScenario] = useState(false);
  const [sharingId, setSharingId] = useState(null);

  // Fetch catalog + multipliers on first open
  useEffect(() => {
    if (!open) return;
    api.get("/pricing/config").then((r) => {
      const rows = (r.data?.rows || []).filter(x => x.active !== false);
      setCatalog(rows);
      const mults = (r.data?.multipliers || []).filter(x => x.active !== false);
      setMultipliers(mults);
      // Pick SEMANA (8h/dia) as default if present
      const def = rows.find(r => r.include_by_default) || rows[0];
      if (def) setRowId(def.id);
    }).catch(() => {});
    // Load saved scenarios so operator can pick one from the top dropdown
    api.get("/pricing/scenarios")
      .then((r) => setScenarios(r.data?.scenarios || []))
      .catch(() => {});
  }, [open]);

  // Debounced simulation whenever any input changes
  useEffect(() => {
    if (!open || !rowId) { setResult(null); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.post("/pricing/simulate", {
        row_id: rowId,
        monthly_cost: Number(monthlyCost) || 0,
        multiplier_ids: selectedMultIds,
      })
      .then((r) => setResult(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro simulador"))
      .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, rowId, monthlyCost, selectedMultIds]);

  const selectedRow = useMemo(() => catalog.find(r => r.id === rowId), [catalog, rowId]);

  const filteredCatalog = useMemo(() => {
    const q = (catalogSearch || "").trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(r => {
      const hay = [r.name, `${r.hours_per_day}h/dia`, `${r.days_per_week}d/sem`].join(" ").toLowerCase();
      return q.split(/\s+/).every(tok => hay.includes(tok));
    });
  }, [catalog, catalogSearch]);

  const toggleMult = (mid) => {
    setSelectedMultIds(prev => prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid]);
  };

  // ---- Scenario handlers ----
  const openSaveModal = () => {
    if (!result) { toast.error("Simule primeiro"); return; }
    setScenarioName("");
    setScenarioDesc("");
    setShowSaveModal(true);
  };

  const saveScenario = async () => {
    if (!scenarioName.trim()) { toast.error("Nome obrigatório"); return; }
    setSavingScenario(true);
    try {
      const config = {
        row_id: rowId,
        monthly_cost: Number(monthlyCost) || 0,
        multiplier_ids: selectedMultIds,
        snapshot: result || null,
      };
      const r = await api.post("/pricing/scenarios", {
        name: scenarioName.trim(),
        description: scenarioDesc.trim() || null,
        config,
      });
      setScenarios((prev) => [r.data.scenario, ...prev]);
      toast.success(`Cenário "${scenarioName}" guardado`);
      setShowSaveModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSavingScenario(false);
    }
  };

  const loadScenario = (id) => {
    if (!id) return;
    const s = scenarios.find(x => x.id === id);
    if (!s) return;
    const cfg = s.config || {};
    if (cfg.row_id) setRowId(cfg.row_id);
    if (typeof cfg.monthly_cost === "number") setMonthlyCost(cfg.monthly_cost);
    setSelectedMultIds(cfg.multiplier_ids || []);
    toast.success(`Cenário "${s.name}" carregado`);
  };

  const deleteScenario = async (id, name) => {
    if (!window.confirm(`Eliminar cenário "${name}"?`)) return;
    try {
      await api.delete(`/pricing/scenarios/${id}`);
      setScenarios((prev) => prev.filter(x => x.id !== id));
      toast.success("Cenário eliminado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const shareScenario = async (s) => {
    setSharingId(s.id);
    try {
      let token = s.share_token;
      if (!token) {
        const r = await api.post(`/pricing/scenarios/${s.id}/share`);
        token = r.data.share_token;
        setScenarios((prev) => prev.map(x => x.id === s.id ? { ...x, share_token: token } : x));
      }
      const url = `${window.location.origin}/scenario/${token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado — envia ao colega");
      } catch {
        window.prompt("Copia o link:", url);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar link");
    } finally {
      setSharingId(null);
    }
  };

  const unshareScenario = async (s) => {
    if (!window.confirm(`Revogar o link partilhado de "${s.name}"?`)) return;
    try {
      await api.delete(`/pricing/scenarios/${s.id}/share`);
      setScenarios((prev) => prev.map(x => x.id === s.id ? { ...x, share_token: null } : x));
      toast.success("Link revogado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const copyToClipboard = async () => {
    if (!result) { toast.error("Simule primeiro"); return; }
    const lines = [
      `SIMULAÇÃO DE PREÇOS — CARING`,
      `Serviço: ${result.row_name}`,
      `Configuração: ${result.days_per_week} dias × ${result.hours_per_day}h/dia (${result.monthly_hours.toFixed(0)}h/mês)`,
      `Preço/hora: ${result.hourly_price?.toFixed(2)}€`,
      `Mensal base: ${result.monthly_revenue_base?.toFixed(2)}€`,
    ];
    if (result.multipliers?.length) {
      lines.push(`Factores:`);
      result.multipliers.forEach(m => lines.push(`  • ${m.name} +${m.percentage}%`));
      lines.push(`Contribuição factores: +${result.multipliers_contribution?.toFixed(2)}€`);
    }
    lines.push(``);
    lines.push(`>> PREÇO TOTAL AO CLIENTE: ${result.monthly_revenue?.toFixed(2)}€/mês <<`);
    if (result.monthly_af_cost > 0) {
      lines.push(``);
      lines.push(`Custo AF: ${result.monthly_af_cost.toFixed(2)}€`);
      lines.push(`Margem: ${result.margin_pct?.toFixed(1)}% (mínimo ${result.threshold_pct}%)`);
      lines.push(`Margem absoluta: ${result.monthly_absolute_margin?.toFixed(2)}€`);
      lines.push(`${result.threshold_ok ? "✓ dentro do threshold" : "⚠ ABAIXO — requere Autorização Especial"}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copiado para clipboard");
    } catch {
      toast.error("Não consegui copiar");
    }
  };

  // ---- PDF export ------------------------------------------------------
  // Reuses the same simulation payload the drawer sends to /simulate,
  // plus optional presentation metadata (title, client, notes) captured
  // in a small modal. The backend recomputes the numbers and returns a
  // branded PDF ready to hand to a prospect before the formal Estimate.
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfClient, setPdfClient] = useState("");
  const [pdfNotes, setPdfNotes] = useState("");
  const [pdfIncludeInternal, setPdfIncludeInternal] = useState(false);

  const openPdfModal = () => {
    if (!result) { toast.error("Simule primeiro"); return; }
    // Sensible defaults so the operator can just hit "Gerar PDF"
    const rowName = result.row_name || "Simulação";
    setPdfTitle(`Simulação · ${rowName}`);
    setPdfClient("");
    setPdfNotes("");
    setPdfIncludeInternal(false);
    setPdfModalOpen(true);
  };

  const exportPdf = async () => {
    if (!result) return;
    setPdfBusy(true);
    try {
      const resp = await api.post(
        "/pricing/simulate/pdf",
        {
          row_id: rowId || null,
          days_per_week: result.days_per_week,
          hours_per_day: result.hours_per_day,
          hourly_price: result.hourly_price,
          monthly_cost: Number(monthlyCost) || 0,
          multiplier_ids: selectedMultIds,
          scenario_title: (pdfTitle || "Simulação").trim(),
          client_name: pdfClient.trim() || null,
          notes: pdfNotes.trim() || null,
          include_internal: pdfIncludeInternal,
        },
        { responseType: "blob" },
      );
      // Trigger download in-browser
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeTitle = (pdfTitle || "simulacao").trim().replace(/\s+/g, "_");
      a.download = `Caring-Simulacao-${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF gerado — download iniciado");
      setPdfModalOpen(false);
    } catch (e) {
      // Blob errors don't carry a JSON body — read the detail if present.
      let detail = "Erro a gerar PDF";
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          detail = JSON.parse(txt)?.detail || detail;
        } else {
          detail = e.response?.data?.detail || detail;
        }
      } catch { /* keep default */ }
      toast.error(detail);
    } finally {
      setPdfBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="simulator-drawer">
      <button
        aria-label="Fechar"
        onClick={onClose}
        className="flex-1 bg-foreground/30 backdrop-blur-sm"
        data-testid="simulator-backdrop"
      />
      <div className="relative w-full sm:w-[520px] bg-card h-full shadow-2xl border-l border-border flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary" />
            <div>
              <div className="font-heading text-sm text-foreground">Simulador de Preços</div>
              <div className="text-[10px] text-muted-foreground">Catálogo 2026 · regras 36/38/40 %</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="simulator-close" className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Pontual reference banner — só aparece quando o Consultor
              abriu o simulador a partir de um pontual valorizado.
              Mostra os totais fixados pelo Consultor para comparar
              com os cenários alternativos abaixo. */}
          {pontualContext && (
            <div
              data-testid="simulator-pontual-context"
              className="rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-4"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[hsl(var(--success))] mb-1.5">
                A comparar contra pontual valorizado
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--success))] font-semibold">Total</div>
                  <div className="font-ui text-lg text-[hsl(var(--success))] tabular-nums">
                    {fmt(pontualContext.total_value || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--success))] font-semibold">Horas</div>
                  <div className="font-ui text-lg text-[hsl(var(--success))] tabular-nums">
                    {Number(pontualContext.total_hours || 0).toLocaleString("pt-PT", { minimumFractionDigits: 1 })}h
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--success))] font-semibold">€/hora</div>
                  <div className="font-ui text-lg text-[hsl(var(--success))] tabular-nums">
                    {fmt(pontualContext.hourly_price || 0).replace(" €", "")}/h
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--success))] mt-2 leading-relaxed">
                Use o simulador abaixo para testar alternativas: escolha uma linha do catálogo
                com hourly próximo, ajuste o custo mensal e compare a margem contra o valor total
                de <b>{fmt(pontualContext.total_value || 0)}</b> acima.
              </p>
            </div>
          )}

          {/* Scenario loader — visible when at least one is saved */}
          {scenarios.length > 0 && (
            <div className="bg-[hsl(var(--info-bg))] border border-primary/20 rounded-lg p-3" data-testid="scenario-loader">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-heading font-semibold flex items-center gap-1.5">
                  <FolderOpen className="w-3.5 h-3.5" /> Cenários guardados ({scenarios.length})
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  onChange={(e) => loadScenario(e.target.value)}
                  data-testid="scenario-picker"
                  className="flex-1 bg-card border border-border rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-primary"
                  defaultValue=""
                >
                  <option value="">Carregar cenário…</option>
                  {scenarios.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.description ? ` — ${s.description.slice(0, 60)}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-2 max-h-32 overflow-y-auto text-[11px] text-muted-foreground divide-y divide-slate-200">
                {scenarios.slice(0, 8).map(s => {
                  const isPersonal = s.visibility === "personal";
                  const isShared = !!s.share_token;
                  return (
                    <div key={s.id} className="flex items-center justify-between py-1.5 gap-2" data-testid={`scenario-item-${s.id}`}>
                      <div className="truncate flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isPersonal
                            ? <Lock className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" title="Pessoal" />
                            : <Users className="w-2.5 h-2.5 text-primary flex-shrink-0" title="Equipa" />}
                          <b className="truncate">{s.name}</b>
                          {isShared && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] font-mono uppercase tracking-wider" title="Link público activo">
                              link
                            </span>
                          )}
                        </div>
                        {s.created_by_name && (
                          <div className="text-[10px] text-muted-foreground truncate">por {s.created_by_name}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => shareScenario(s)}
                          disabled={sharingId === s.id}
                          title={isShared ? "Copiar link partilhado" : "Gerar link partilhado"}
                          className={`p-1 rounded ${isShared ? "text-[hsl(var(--success))] hover:text-[hsl(var(--success))]" : "text-muted-foreground hover:text-primary"}`}
                          data-testid={`scenario-share-${s.id}`}
                        >
                          {sharingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
                        </button>
                        {isShared && (
                          <button
                            onClick={() => unshareScenario(s)}
                            title="Revogar link"
                            className="p-1 rounded text-muted-foreground/70 hover:text-[hsl(var(--warning))]"
                            data-testid={`scenario-unshare-${s.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteScenario(s.id, s.name)}
                          title="Eliminar cenário"
                          className="p-1 rounded text-muted-foreground/70 hover:text-rose-500"
                          data-testid={`scenario-delete-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row picker with search */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Serviço</label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={catalogSearch}
                onChange={(e)=>setCatalogSearch(e.target.value)}
                placeholder="Pesquisar (semana 8, noites…)"
                className="w-full pl-8 pr-2 py-1.5 text-xs bg-card border border-border rounded-md focus:outline-none focus:border-primary"
                data-testid="simulator-search"
              />
            </div>
            <select
              value={rowId}
              onChange={(e) => setRowId(e.target.value)}
              data-testid="simulator-row"
              className="w-full mt-2 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
            >
              {filteredCatalog.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} · {r.hourly_price.toFixed(2)}€/h
                </option>
              ))}
            </select>
            {selectedRow && (
              <div className="mt-1.5 text-[11px] text-muted-foreground">
                {selectedRow.days_per_week} dias × {selectedRow.hours_per_day}h/dia · {(selectedRow.days_per_week * selectedRow.hours_per_day * 4.33).toFixed(0)}h/mês
              </div>
            )}
          </div>

          {/* Monthly cost */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Custo mensal (AF) €</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={monthlyCost}
              onChange={(e)=>setMonthlyCost(e.target.value)}
              data-testid="simulator-cost"
              className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
              placeholder="Ex: 600"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Este é o custo total mensal para a Caring (salários + encargos AF). Serve para calcular a margem.
            </div>
          </div>

          {/* Multipliers */}
          {multipliers.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Factores multiplicadores</label>
              <div className="mt-2 grid grid-cols-1 gap-1.5">
                {multipliers.map(m => {
                  const on = selectedMultIds.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      data-testid={`simulator-mult-${m.id}`}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${on ? "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/40" : "bg-card border-border hover:bg-muted/40"}`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleMult(m.id)}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground">{m.name}</div>
                          {m.note && <div className="text-[11px] text-muted-foreground">{m.note}</div>}
                        </div>
                      </div>
                      <div className="font-mono text-sm text-[hsl(var(--warning))] font-semibold">+{m.percentage}%</div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Result card */}
          {result && (
            <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-3" data-testid="simulator-result">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Resultado</div>
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Preço/hora</div>
                  <div className="font-display text-xl text-foreground" data-testid="sim-hourly">{fmt(result.hourly_price)}</div>
                </div>
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Mensal base</div>
                  <div className="font-display text-xl text-foreground" data-testid="sim-monthly-base">{fmt(result.monthly_revenue_base)}</div>
                </div>
                {result.multipliers_contribution > 0 && (
                  <div className="col-span-2 bg-[hsl(var(--warning-bg))] rounded-lg p-3 border border-[hsl(var(--warning))]/25 flex items-center justify-between">
                    <div className="text-sm text-[hsl(var(--warning))]">
                      Factores <span className="font-mono">+{result.multipliers_total_pct.toFixed(1)}%</span>
                    </div>
                    <div className="font-mono text-[hsl(var(--warning))] font-semibold">+ {fmt(result.multipliers_contribution)}</div>
                  </div>
                )}
                <div className="col-span-2 bg-foreground text-background rounded-lg p-3">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-primary-foreground/60">Preço total ao cliente / mês</div>
                  <div className="font-display text-2xl" data-testid="sim-monthly-total">{fmt(result.monthly_revenue)}</div>
                </div>
              </div>

              {/* Margin analysis */}
              {result.monthly_af_cost > 0 && (
                <div className={`rounded-lg p-3 border ${result.threshold_ok ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25"}`} data-testid="sim-margin">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {result.threshold_ok
                        ? <ShieldCheck className="w-4 h-4 text-[hsl(var(--success))]" />
                        : <AlertTriangle className="w-4 h-4 text-[hsl(var(--danger))]" />}
                      <div>
                        <div className={`text-[10px] uppercase tracking-[0.15em] font-heading ${result.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                          Margem
                        </div>
                        <div className={`font-display text-xl ${result.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                          {result.margin_pct.toFixed(1)}% <span className="text-sm">/ min {result.threshold_pct}%</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Absoluta: <b>{fmt(result.monthly_absolute_margin)}</b> — categoria "{result.service_category.replace('_', ' ')}"
                        </div>
                      </div>
                    </div>
                    <TrendingUp className={`w-6 h-6 ${result.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`} />
                  </div>
                  {result.requires_authorization && (
                    <div className="mt-2 text-[12px] text-[hsl(var(--danger))] font-semibold">
                      ⚠ Abaixo do mínimo — requere <b>Autorização Especial</b> (Rui Pinto ou Daniela Monteiro).
                    </div>
                  )}
                </div>
              )}
              {result.monthly_af_cost === 0 && (
                <div className="text-[11px] text-muted-foreground italic">
                  Insira o custo mensal (AF) para ver a análise de margem.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-card p-3 flex items-center justify-between gap-2">
          <div className="text-[10px] text-muted-foreground text-center flex-1">
            Simulador standalone · não altera nenhum lead
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={copyToClipboard}
              disabled={!result}
              data-testid="btn-copy-scenario"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 border border-border rounded-md px-2 py-1.5"
              title="Copiar resumo para clipboard"
            >
              <Copy className="w-3 h-3" /> Copiar
            </button>
            <button
              onClick={openPdfModal}
              disabled={!result}
              data-testid="btn-export-pdf"
              className="inline-flex items-center gap-1 text-[11px] text-foreground hover:text-foreground disabled:opacity-30 border border-border bg-card rounded-md px-2 py-1.5"
              title="Gerar PDF pronto para enviar ao cliente"
            >
              <FileDown className="w-3 h-3" /> Exportar PDF
            </button>
            <button
              onClick={openSaveModal}
              disabled={!result}
              data-testid="btn-save-scenario"
              className="inline-flex items-center gap-1 text-[11px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-md px-2.5 py-1.5 font-heading font-semibold disabled:opacity-30"
              title="Guardar esta simulação como cenário"
            >
              <Save className="w-3 h-3" /> Guardar
            </button>
          </div>
        </div>

        {/* Export-PDF modal — collects presentation metadata that the
            backend embeds in the branded printable PDF. */}
        {pdfModalOpen && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-foreground/40 backdrop-blur-sm"
            data-testid="scenario-pdf-modal"
          >
            <div className="bg-card rounded-2xl shadow-2xl border border-border w-[420px] max-w-[92%] p-5 space-y-4">
              <div>
                <h4 className="font-heading text-foreground flex items-center gap-2">
                  <FileDown className="w-4 h-4 text-primary" />
                  Exportar PDF
                </h4>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Gera uma proposta preliminar em PDF pronta para enviar ao cliente.
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                  Título do cenário
                </label>
                <input
                  data-testid="pdf-scenario-title"
                  className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  value={pdfTitle}
                  onChange={(e) => setPdfTitle(e.target.value)}
                  placeholder="Ex: Cenário Sra. Maria — turno completo"
                  disabled={pdfBusy}
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                  Cliente (opcional)
                </label>
                <input
                  data-testid="pdf-client-name"
                  className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  value={pdfClient}
                  onChange={(e) => setPdfClient(e.target.value)}
                  placeholder="Maria Silva"
                  disabled={pdfBusy}
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                  Notas (opcional)
                </label>
                <textarea
                  data-testid="pdf-notes"
                  className="w-full mt-1 bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  rows={3}
                  value={pdfNotes}
                  onChange={(e) => setPdfNotes(e.target.value)}
                  placeholder="Preferências, particularidades…"
                  disabled={pdfBusy}
                  maxLength={500}
                />
              </div>
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                <input
                  data-testid="pdf-include-internal"
                  type="checkbox"
                  checked={pdfIncludeInternal}
                  onChange={(e) => setPdfIncludeInternal(e.target.checked)}
                  disabled={pdfBusy}
                />
                <span>
                  Incluir análise interna (margem, custo AF) — <b>não partilhar com cliente</b>
                </span>
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  data-testid="pdf-cancel"
                  onClick={() => setPdfModalOpen(false)}
                  disabled={pdfBusy}
                  className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  data-testid="pdf-confirm"
                  onClick={exportPdf}
                  disabled={pdfBusy || !pdfTitle.trim()}
                  className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                  Gerar PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save-scenario modal */}
        {showSaveModal && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-foreground/40 backdrop-blur-sm" data-testid="scenario-save-modal">
            <div className="bg-card rounded-2xl shadow-2xl border border-border w-[400px] max-w-[90%] p-5 space-y-4">
              <div>
                <h4 className="font-heading text-foreground">Guardar cenário</h4>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Guarda esta combinação para reutilizar depois.
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Nome*</label>
                <input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  data-testid="scenario-name-input"
                  className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  placeholder="Ex: Sr. Santos - noite alzheimer"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Descrição (opcional)</label>
                <textarea
                  rows={2}
                  value={scenarioDesc}
                  onChange={(e) => setScenarioDesc(e.target.value)}
                  data-testid="scenario-desc-input"
                  className="w-full mt-1 bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none"
                  placeholder="Contexto — família, preferências, restrições…"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Visibilidade</label>
                <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="scenario-visibility-toggle">
                  <button
                    type="button"
                    onClick={() => setScenarioVisibility("team")}
                    data-testid="scenario-visibility-team"
                    className={`flex items-start gap-2 rounded-lg border p-2.5 text-left transition-colors ${scenarioVisibility === "team" ? "bg-[hsl(var(--info-bg))] border-primary text-foreground" : "bg-card border-border text-muted-foreground hover:border-border"}`}
                  >
                    <Users className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${scenarioVisibility === "team" ? "text-primary" : "text-muted-foreground"}`} />
                    <div>
                      <div className="text-xs font-heading font-semibold">Equipa</div>
                      <div className="text-[10px] leading-snug opacity-80">Todos os gestores e admins vêem</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setScenarioVisibility("personal")}
                    data-testid="scenario-visibility-personal"
                    className={`flex items-start gap-2 rounded-lg border p-2.5 text-left transition-colors ${scenarioVisibility === "personal" ? "bg-[hsl(var(--info-bg))] border-primary text-foreground" : "bg-card border-border text-muted-foreground hover:border-border"}`}
                  >
                    <Lock className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${scenarioVisibility === "personal" ? "text-primary" : "text-muted-foreground"}`} />
                    <div>
                      <div className="text-xs font-heading font-semibold">Pessoal</div>
                      <div className="text-[10px] leading-snug opacity-80">Só tu vês (partilhável por link)</div>
                    </div>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="text-[12px] text-muted-foreground hover:text-foreground px-3 py-2"
                  data-testid="scenario-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveScenario}
                  disabled={savingScenario || !scenarioName.trim()}
                  data-testid="scenario-save-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground px-4 py-2 text-sm font-heading font-semibold"
                >
                  {savingScenario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
